import type { FC } from "react"
import React, { useState } from "react"
import type { GetServerSideProps } from "next"
import { Stack } from "@chakra-ui/react"

import type { Governor, Organization } from "query/graphql"
import { getOrganizationSocialPreviewImage } from "organization/helpers/organization"
import { OrganizationProvider } from "organization/providers/OrganizationProvider"
import MainLayout from "ui/layouts/main/MainLayout"
import MainLayoutHead from "ui/layouts/main/MainLayoutHead"
import MainLayoutHeader from "ui/layouts/main/MainLayoutHeader"
import MainLayoutFooter from "ui/layouts/main/MainLayoutFooter"
import MainLayoutContainer from "ui/layouts/main/MainLayoutContainer"
import GovernanceDelegatesLayout from "governance/components/GovernanceDelegatesLayout"
import ErrorBoundary from "common/components/ErrorBoundary"
import { BREADCRUMB } from "common/constants/breadcrumb"
import GovernanceProvider from "governance/context"
import { isValidAccountId } from "web3/helpers/accountId"
import { fetchGovernanceById } from "governance/helpers/fetch"
import { ROUTES } from "common/constants/routes"
import type { OrganizationAnnouncement } from "organization/helpers/fetcher"
import {
  fetchGovernorsByOrganization,
  fetchOrganizationAnnouncement,
  fetchOrganizationBySlug,
} from "organization/helpers/fetcher"
import { isCorrectSlug, isWhitelabel } from "whitelabel/hooks/useIsWhiteLabel"
import {
  getWhiteLabelRoute,
  getWhitelabelBreadcrumb,
} from "whitelabel/utils/breadcrumb"
import GovernanceMembersNavTabs from "governance/components/GovernanceMembersNavTabs"
import { OrganizationMenu } from "ui/layouts/main/header/HeaderDAO"
import DaoLayoutBreadcrumb from "ui/layouts/dao/DaoLayoutBreadcrumb"
import { spacing } from "theme/foundations/spacing"

type Props = {
  governors: Governor[]
  organization: Organization
  organizationAnnouncement?: OrganizationAnnouncement
  isWhiteLabel: boolean
}

const GovernanceDelegates: FC<Props> = ({
  governors,
  organization,
  organizationAnnouncement,
  isWhiteLabel,
}) => {
  const [governor] = governors
  const { slug, name } = organization
  const [isMobileModalOpen, setIsMobileModalOpen] = useState(false)

  // Set any title
  const title = isWhiteLabel
    ? `${name} | Delegates`
    : `Tally | ${name} | Delegates`
  const preview = {
    title,
    image: getOrganizationSocialPreviewImage(organization),
    description: `Explore ${name} delegates`,
  }

  const breadcrumb = isWhiteLabel
    ? getWhitelabelBreadcrumb(
        BREADCRUMB.governance.delegates(slug, name, isWhiteLabel),
      )
    : BREADCRUMB.governance.delegates(slug, name, isWhiteLabel)

  return (
    <ErrorBoundary section="Governance">
      <OrganizationProvider organization={organization}>
        <GovernanceProvider governor={governor}>
          <MainLayout>
            <MainLayoutHead preview={preview} title={title} />
            <MainLayoutHeader
              activeOrganizationMenu={OrganizationMenu.Community}
              isWhiteLabel={isWhiteLabel}
              organization={organization}
            />
            <DaoLayoutBreadcrumb breadcrumb={breadcrumb} />
            <MainLayoutContainer>
              <Stack spacing={spacing.containerGap}>
                <ErrorBoundary section="MembersNavTabs">
                  <GovernanceMembersNavTabs
                    governor={governor}
                    organizationId={organization.id}
                    slug={slug}
                    onOpenInfoModal={() => {
                      setIsMobileModalOpen(true)
                    }}
                  />
                </ErrorBoundary>
                <ErrorBoundary section="GovernanceDelegates">
                  <GovernanceDelegatesLayout
                    governorSlug={slug}
                    governors={governors}
                    isMobileModalOpen={isMobileModalOpen}
                    isWhiteLabel={isWhiteLabel}
                    organization={organization}
                    organizationAnnouncement={organizationAnnouncement}
                    setIsMobileModalOpen={setIsMobileModalOpen}
                  />
                </ErrorBoundary>
              </Stack>
            </MainLayoutContainer>
            <MainLayoutFooter isWhiteLabel={isWhiteLabel} />
          </MainLayout>
        </GovernanceProvider>
      </OrganizationProvider>
    </ErrorBoundary>
  )
}

export const getServerSideProps: GetServerSideProps = async (context) => {
  const { query } = context
  const { governanceId: governanceIdParam } = query
  const slug = governanceIdParam as string

  // Find governance by id and redirect to /gov/:slug
  if (isValidAccountId(slug)) {
    const governance = await fetchGovernanceById(slug)

    if (governance && governance.organization) {
      return {
        redirect: {
          permanent: false,
          destination: isWhitelabel(context)
            ? getWhiteLabelRoute(
                ROUTES.governance.delegates(governance.organization.slug),
              )
            : ROUTES.governance.delegates(governance.organization.slug),
        },
      }
    }
  }

  if (isWhitelabel(context)) {
    if (!isCorrectSlug(context, slug)) {
      return {
        redirect: {
          permanent: false,
          destination: "/404",
        },
      }
    }
  }

  const organization = await fetchOrganizationBySlug(slug, context)

  if (!organization) {
    return {
      redirect: {
        permanent: false,
        destination: "/404",
      },
    }
  }

  const governors = await fetchGovernorsByOrganization(organization.id, context)

  const organizationAnnouncement = await fetchOrganizationAnnouncement({
    organizationId: organization.id,
  })

  return {
    props: {
      organization,
      governors,
      organizationAnnouncement,
      isWhiteLabel: isWhitelabel(context),
    },
  }
}

export default GovernanceDelegates
